import { useEffect, useState } from "react";
import { EmailSignature } from "@/components/EmailSignature";
import { Navigate } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const cell = (c, r) => (c.fmt ? c.fmt(r[c.key]) : r[c.key] || "");

const toCsv = (rows, cols) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.map((c) => esc(c.label)).join(","), ...rows.map((r) => cols.map((c) => esc(cell(c, r))).join(","))].join("\n");
};

const LABELS = {
  counselling: { en: "Individual counselling", fr: "Counseling individuel" },
  consulting: { en: "Consulting & training", fr: "Services-conseils et formation" },
  media: { en: "Media", fr: "Médias" },
  groups: { en: "Group programs", fr: "Programmes de groupe" },
  regulation_recovery: { en: "Regulation & Recovery (DBT)", fr: "Régulation et rétablissement (TCD)" },
};

const STATUSES = {
  new: { en: "New", fr: "Nouveau" },
  contacted: { en: "Contacted", fr: "Contacté" },
  booked: { en: "Booked", fr: "Rendez-vous pris" },
  closed: { en: "Not proceeding", fr: "Sans suite" },
};

const StatusSelect = ({ kind, row, onChange }) => {
  const { t } = useLang();
  const change = async (status) => {
    try {
      await api.patch(`/admin/waitlist/${kind}/${row.id}`, { status });
      onChange(row.id, status);
    } catch {
      toast.error(t({ en: "Could not update the status.", fr: "Impossible de mettre à jour le statut." }));
    }
  };
  return (
    <select value={row.status || "new"} onChange={(e) => change(e.target.value)} className="p-2 rounded-lg border text-sm" style={{ borderColor: "var(--border-medium)", background: "var(--surface)" }} data-testid={`admin-${kind}-status-${row.id}`}>
      {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
    </select>
  );
};

const StatusFilter = ({ id, rows, value, onChange }) => {
  const { t } = useLang();
  const count = (k) => rows.filter((r) => (r.status || "new") === k).length;
  const opts = [["all", { en: "All", fr: "Tous" }, rows.length], ...Object.entries(STATUSES).map(([k, v]) => [k, v, count(k)])];
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid={`admin-${id}-filter`}>
      {opts.map(([k, l, n]) => (
        <button key={k} onClick={() => onChange(k)} className="px-3 py-1.5 rounded-full border text-xs transition-colors" style={{ borderColor: "var(--border-medium)", background: value === k ? "var(--brand)" : "transparent", color: value === k ? "#fff" : "var(--ink-2)" }} data-testid={`admin-${id}-filter-${k}`}>
          {t(l)} ({n})
        </button>
      ))}
    </div>
  );
};

const downloadCsv = (name, rows, cols) => {
  const url = URL.createObjectURL(new Blob(["\ufeff" + toCsv(rows, cols)], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const WaitlistTable = ({ id, title, rows: allRows, cols: baseCols, onStatus }) => {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");
  const rows = filter === "all" ? allRows : allRows.filter((r) => (r.status || "new") === filter);
  const cols = [{ key: "status", label: t({ en: "Status", fr: "Statut" }), fmt: (v) => t(STATUSES[v || "new"]) }, ...baseCols];
  return (
    <section className="mt-12" data-testid={`admin-${id}-section`}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="font-serif text-2xl" style={{ color: "var(--ink)" }}>{title} <span className="text-base" style={{ color: "var(--ink-3)" }} data-testid={`admin-${id}-count`}>({rows.length})</span></h2>
        <button onClick={() => downloadCsv(`${id}-waitlist${filter === "all" ? "" : `-${filter}`}.csv`, rows, cols)} disabled={!rows.length} className="btn-accent text-sm disabled:opacity-40" data-testid={`admin-${id}-download`}>
          <Download size={14} /> {t({ en: "Download spreadsheet", fr: "Télécharger le tableur" })}
        </button>
      </div>
      <StatusFilter id={id} rows={allRows} value={filter} onChange={setFilter} />
      <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: "var(--border-medium)" }}>
        <table className="w-full text-sm text-left">
          <thead style={{ background: "var(--surface)" }}>
            <tr>{cols.map((c) => <th key={c.key} className="p-3 font-medium whitespace-nowrap" style={{ color: "var(--ink-2)" }}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={cols.length} className="p-6 text-center" style={{ color: "var(--ink-3)" }}>{allRows.length ? t({ en: "No sign-ups with this status.", fr: "Aucune inscription avec ce statut." }) : t({ en: "No sign-ups yet.", fr: "Aucune inscription pour l'instant." })}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t align-top" style={{ borderColor: "var(--border-subtle)" }} data-testid={`admin-${id}-row`}>
                {cols.map((c) => <td key={c.key} className={`p-3 ${c.key === "message" ? "min-w-[240px] whitespace-pre-wrap" : "whitespace-nowrap"}`} style={{ color: "var(--ink-2)" }}>{c.key === "status" ? <StatusSelect kind={id} row={r} onChange={onStatus} /> : cell(c, r) || "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const ChangePassword = () => {
  const { t } = useLang();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success(t({ en: "Password updated.", fr: "Mot de passe mis à jour." }));
      setCur(""); setNext("");
    } catch (err) {
      toast.error(err.response?.status === 400 ? t({ en: "Current password is incorrect.", fr: "Le mot de passe actuel est incorrect." }) : t({ en: "New password must be at least 8 characters.", fr: "Le nouveau mot de passe doit contenir au moins 8 caractères." }));
    } finally { setBusy(false); }
  };
  const field = "w-full p-3 rounded-lg border";
  const style = { borderColor: "var(--border-medium)", background: "var(--surface)" };
  return (
    <section className="mt-16 max-w-md" data-testid="admin-change-password">
      <h2 className="font-serif text-2xl mb-4" style={{ color: "var(--ink)" }}>{t({ en: "Change your password", fr: "Changer votre mot de passe" })}</h2>
      <form onSubmit={submit} className="space-y-3">
        <input type="password" required placeholder={t({ en: "Current password", fr: "Mot de passe actuel" })} value={cur} onChange={(e) => setCur(e.target.value)} className={field} style={style} data-testid="admin-current-password" />
        <input type="password" required minLength={8} placeholder={t({ en: "New password (8+ characters)", fr: "Nouveau mot de passe (8 caractères min.)" })} value={next} onChange={(e) => setNext(e.target.value)} className={field} style={style} data-testid="admin-new-password" />
        <button type="submit" disabled={busy} className="btn-accent text-sm" data-testid="admin-change-password-submit">{t({ en: "Update password", fr: "Mettre à jour" })}</button>
      </form>
    </section>
  );
};

export default function AdminWaitlist() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/waitlist").then((r) => setData(r.data)).catch(() => toast.error(t({ en: "Could not load the waitlist.", fr: "Impossible de charger la liste." })));
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return null;
  if (!user) return <Navigate to="/login?redirect=/admin/waitlist" replace />;
  if (!isAdmin) return <Navigate to="/portal" replace />;

  const date = (v) => (v ? new Date(v).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—");
  const base = [
    { key: "created_at", label: t({ en: "Date", fr: "Date" }), fmt: date },
    { key: "name", label: t({ en: "Name", fr: "Nom" }) },
    { key: "email", label: t({ en: "Email", fr: "Courriel" }) },
    { key: "phone", label: t({ en: "Phone", fr: "Téléphone" }) },
  ];
  const label = (v) => (LABELS[v] ? t(LABELS[v]) : v || "");
  const message = { key: "message", label: t({ en: "Message", fr: "Message" }) };
  const setStatus = (kind) => (rowId, status) => setData((d) => ({ ...d, [kind]: d[kind].map((r) => (r.id === rowId ? { ...r, status } : r)) }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="admin-waitlist-page">
      <div className="overline mb-4">{t({ en: "Private — owner only", fr: "Privé — propriétaire seulement" })}</div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tight" style={{ color: "var(--ink)" }}>{t({ en: "Waitlist", fr: "Liste d'attente" })}</h1>
      {!data ? <p className="mt-8" style={{ color: "var(--ink-3)" }}>{t({ en: "Loading…", fr: "Chargement…" })}</p> : (
        <>
          <WaitlistTable id="practice" title={t({ en: "Practice waitlist", fr: "Liste d'attente du cabinet" })} rows={data.practice} cols={[...base, { key: "interested_in", label: t({ en: "Interest", fr: "Intérêt" }), fmt: label }, message]} onStatus={setStatus("practice")} />
          <WaitlistTable id="groups" title={t({ en: "Group program waitlist", fr: "Liste d'attente des groupes" })} rows={data.groups} cols={[...base, { key: "program", label: t({ en: "Program", fr: "Programme" }), fmt: label }, message]} onStatus={setStatus("groups")} />
        </>
      )}
      <EmailSignature />
      <ChangePassword />
    </div>
  );
}
